// @components/LaunchDarklyWrapper.tsx
import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { launchDarklyConfig } from '@api/auth/config';
import CircularProgress from '@mui/material/CircularProgress';

// Module-level singleton — ensures asyncWithLDProvider is called exactly once
// regardless of StrictMode double-invoke or re-renders.
let ldProviderPromise: Promise<React.ComponentType<{ children: ReactNode }>> | null = null;

function getOrInitLDProvider() {
  if (!ldProviderPromise) {
    ldProviderPromise = asyncWithLDProvider({
      clientSideID: launchDarklyConfig.clientId ?? '',
      context: {
        kind: 'user',
        anonymous: true,
      },
      timeout: 3,
      options: {
        logger: {
          debug: () => {},
          info: () => {},
          warn: console.warn,
          error: console.error,
        },
      },
    });
  }
  return ldProviderPromise;
}

export const LaunchDarklyWrapper = ({ children }: { children: ReactNode }) => {
  const [LDProvider, setLDProvider] = useState<React.ComponentType<{ children: ReactNode }> | null>(null);
  const [timedOutOrFailed, setTimedOutOrFailed] = useState(false);
  const hasProvider = useRef(false);

  useEffect(() => {
    let isMounted = true;

    // Safety net: If LD takes longer than 2.5 seconds, trigger the fallback path
    const fallbackTimer = setTimeout(() => {
      if (isMounted && !hasProvider.current) {
        console.warn('LaunchDarkly initialization timed out. Falling back to application defaults.');
        setTimedOutOrFailed(true);
      }
    }, 2500);

    async function initLD() {
      try {
        const Provider = await getOrInitLDProvider();

        if (isMounted) {
          clearTimeout(fallbackTimer);
          hasProvider.current = true;
          setLDProvider(() => Provider);
        }
      } catch (error) {
        console.error('LaunchDarkly initialization failed entirely:', error);
        if (isMounted) {
          clearTimeout(fallbackTimer);
          setTimedOutOrFailed(true);
        }
      }
    }

    initLD();

    return () => {
      isMounted = false;
      clearTimeout(fallbackTimer);
    };
  }, []);

  // 1. If LD initialized successfully, use it.
  if (LDProvider) {
    return <LDProvider>{children}</LDProvider>;
  }

  // 2. If LD failed or timed out, mount the App directly.
  // Code-level defaults inside your components will govern the UI.
  if (timedOutOrFailed) {
    return <>{children}</>;
  }

  // 3. Show this only while the network request is healthily in flight
  return (
    <div
      className='app-loading-container'
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
      }}
    >
      <CircularProgress />
    </div>
  );
};
